/**
 * @packageDocumentation
 * @module Auth
 * @category Core
 */

import { inject, Injectable } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AuthService } from '../services/auth.service';

/**
 * Guard для проверки факта авторизации пользователя.
 *
 * @example
 * ```ts
 * export const ROUTES: Routes = [
 *  {
 *    path: 'example',
 *    component: ExamplePage,
 *    canActivate: [canActivateAuthorized],
 *    canMatch: [canMatchAuthorized]
 *  }
 * ]
 * ```
 *
 * @group Guard
 */
@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  private readonly authService = inject(AuthService);

  private readonly router = inject(Router);

  public checkAuthenticated(): Observable<boolean> {
    return this.authService.authenticated$.pipe(
      map((authenticated) => {
        if (!authenticated) {
          this.router.navigate(['/login']);
          return false;
        }

        return authenticated;
      }),
    );
  }
}

/**
 * Проверка авторизация для доступа к роуту
 *
 * @see {@link AuthGuard}
 *
 * @group Guard
 *
 * @param route
 * @returns
 */
export const canActivateAuthorized: CanActivateFn = () => inject(AuthGuard).checkAuthenticated();

/**
 * Проверка авторизации для возможности открыть роут
 *
 * @see {@link AuthGuard}
 *
 * @group Guard
 *
 * @param route
 * @returns
 */
export const canMatchAuthorized: CanMatchFn = () => inject(AuthGuard).checkAuthenticated();
